import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../lib/api';
import { useAuth } from '../../AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Shield, KeyRound, Copy, RefreshCw, Loader2, Eye, EyeOff, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (iso) => {
  if (!iso) return 'Never rotated';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function TestUsers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(null);     // email currently being rotated
  const [reveal, setReveal] = useState({});           // { email: { password, rotated_at, visible } }
  const [customPwd, setCustomPwd] = useState({});     // { email: string }
  const [settingCustom, setSettingCustom] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/auth/admin/test-users`, { withCredentials: true });
      setRows(r.data?.users || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Super-admin role required to view test users');
      } else {
        toast.error(e?.response?.data?.detail || 'Failed to load test users');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    // eslint-disable-next-line
    load();
  }, [isSuperAdmin]);

  const rotate = async (email) => {
    setRotating(email);
    try {
      const r = await axios.post(
        `${API}/auth/admin/rotate-test-user`,
        { email },
        { withCredentials: true },
      );
      setReveal((s) => ({
        ...s,
        [email]: { password: r.data.password, rotated_at: r.data.rotated_at, visible: true },
      }));
      toast.success(`Rotated password for ${email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rotation failed');
    } finally {
      setRotating(null);
    }
  };

  const setCustom = async (email) => {
    const pwd = (customPwd[email] || '').trim();
    if (pwd.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setSettingCustom(email);
    try {
      const r = await axios.post(
        `${API}/auth/admin/set-password`,
        { email, password: pwd },
        { withCredentials: true },
      );
      setReveal((s) => ({
        ...s,
        [email]: { password: pwd, rotated_at: r.data.rotated_at, visible: true },
      }));
      setCustomPwd((s) => ({ ...s, [email]: '' }));
      toast.success(`Custom password set for ${email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to set password');
    } finally {
      setSettingCustom(null);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copied to clipboard'),
      () => toast.error('Copy failed'),
    );
  };

  if (!isSuperAdmin) {
    return (
      <div className="max-w-2xl mx-auto pt-16" data-testid="test-users-denied">
        <Card className="p-8 text-center border-amber-500/30 bg-amber-500/5">
          <Shield className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-foreground">Super-admin only</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Rotating demo credentials is restricted to super-admins. Ask
            <code className="mx-1 px-1.5 py-0.5 bg-secondary rounded text-xs">sales@cloudgenie.co</code>
            to share a fresh password.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="test-users-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground flex items-center gap-2">
          <KeyRound className="h-6 w-6 text-amber-500" />
          Demo Account Credentials
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Rotate the two seeded password-based test users used for sales demos and QA.
          Passwords are shown <strong>once</strong> after rotation — copy them immediately.
        </p>
      </div>

      <Card className="p-4 border-amber-500/30 bg-amber-500/5 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-muted-foreground leading-relaxed">
          Rotating a password <strong>immediately invalidates all active sessions</strong> for that
          account. Any browser currently signed in as a demo user will be kicked
          back to the login screen.
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading test users…
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((row) => {
            const r = reveal[row.email];
            return (
              <Card key={row.email} className="p-5 space-y-4" data-testid={`test-user-row-${row.email}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-mono text-foreground" data-testid={`test-user-email-${row.email}`}>
                      {row.email}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{row.name}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className={
                        row.role === 'super_admin'
                          ? 'border-purple-500/40 text-purple-300 bg-purple-500/5'
                          : 'border-blue-500/40 text-blue-300 bg-blue-500/5'
                      }>
                        <Shield className="h-3 w-3 mr-1" /> {row.role}
                      </Badge>
                      <span className="text-[11px] text-muted-foreground">
                        Last rotated: {formatDate(row.last_password_rotated_at)}
                      </span>
                    </div>
                  </div>
                  <Button
                    onClick={() => rotate(row.email)}
                    disabled={rotating === row.email}
                    data-testid={`rotate-btn-${row.email}`}
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                  >
                    {rotating === row.email
                      ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      : <RefreshCw className="h-4 w-4 mr-2" />}
                    Generate random
                  </Button>
                </div>

                {r && (
                  <div
                    className="p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5"
                    data-testid={`rotate-result-${row.email}`}
                  >
                    <div className="text-[11px] uppercase tracking-wider text-emerald-400 mb-1.5">
                      New password (shown once)
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        readOnly
                        value={r.visible ? r.password : '••••••••••••••••'}
                        className="font-mono text-sm"
                        data-testid={`pwd-display-${row.email}`}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setReveal((s) => ({
                          ...s, [row.email]: { ...r, visible: !r.visible },
                        }))}
                        title={r.visible ? 'Hide' : 'Show'}
                      >
                        {r.visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copy(r.password)}
                        title="Copy"
                        data-testid={`copy-btn-${row.email}`}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-2">
                      Rotated at {formatDate(r.rotated_at)} — close this card and the
                      password is gone for good.
                    </div>
                  </div>
                )}

                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">
                    Or set a custom password
                  </summary>
                  <div className="flex items-end gap-2 mt-3">
                    <div className="flex-1">
                      <Label className="text-xs">Custom password (min 8 chars)</Label>
                      <Input
                        type="text"
                        value={customPwd[row.email] || ''}
                        onChange={(e) => setCustomPwd((s) => ({ ...s, [row.email]: e.target.value }))}
                        placeholder="e.g. CloudGenie!2026"
                        className="mt-1 font-mono"
                        data-testid={`custom-pwd-input-${row.email}`}
                      />
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setCustom(row.email)}
                      disabled={settingCustom === row.email}
                      data-testid={`set-custom-btn-${row.email}`}
                    >
                      {settingCustom === row.email
                        ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        : null}
                      Apply
                    </Button>
                  </div>
                </details>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
